import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  deleteCompanyDkpTemplate,
  getCompanyDkpTemplateSignedUrl,
  uploadCompanyDkpTemplate,
} from '@/api/companies'
import { Button } from '@/components/ui/Button'
import i18n from '@/utils/i18n'

/**
 * Секция «Шаблон ДКП» в карточке компании.
 *
 * Используется при автогенерации ДКП (pdfgen берёт файл из bucket-а
 * `dkp-templates` по `companies.dkp_template_path`). Здесь — UI для
 * загрузки/замены/удаления шаблона.
 *
 * В отличие от файлов «Факсимиле/Печать», шаблон сохраняется в БД
 * сразу при загрузке (без формы «редактирование/сохранение»), потому что
 * это атомарная операция и редко требует отката.
 */
export interface CompanyDkpTemplateSectionProps {
  companyId: string
  templatePath: string | null
  templateUploadedAt: string | null
  editable: boolean
  onChanged: (next: {
    dkp_template_path: string | null
    dkp_template_uploaded_at: string | null
  }) => void
}

export function CompanyDkpTemplateSection({
  companyId,
  templatePath,
  templateUploadedAt,
  editable,
  onChanged,
}: CompanyDkpTemplateSectionProps) {
  const { t } = useTranslation()
  const [signedUrl, setSignedUrl] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    if (!templatePath) {
      setSignedUrl(null)
      return
    }
    void getCompanyDkpTemplateSignedUrl(templatePath).then((url) => {
      if (!cancelled) setSignedUrl(url)
    })
    return () => {
      cancelled = true
    }
  }, [templatePath])

  async function handleFile(file: File | null) {
    if (!file) return
    setBusy(true)
    setError(null)
    try {
      const path = await uploadCompanyDkpTemplate(companyId, file)
      onChanged({
        dkp_template_path: path,
        dkp_template_uploaded_at: new Date().toISOString(),
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : t('companies.dkp.uploadFailed'))
    } finally {
      setBusy(false)
    }
  }

  async function handleDelete() {
    if (!templatePath) return
    if (!confirm(t('companies.dkp.deleteConfirm'))) return
    setBusy(true)
    setError(null)
    try {
      await deleteCompanyDkpTemplate(companyId, templatePath)
      onChanged({ dkp_template_path: null, dkp_template_uploaded_at: null })
    } catch (e) {
      setError(e instanceof Error ? e.message : t('companies.dkp.deleteFailed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <span className="stryzh-input-label">{t('companies.dkp.label')}</span>
      <div className="flex flex-wrap items-center gap-3">
        {signedUrl ? (
          <a
            href={signedUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-accent-orange)] hover:underline"
          >
            {t('companies.dkp.downloadCurrent')}
          </a>
        ) : (
          <span className="text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-secondary)]">
            {templatePath ? t('companies.dkp.uploadedNoAccess') : t('companies.dkp.notUploaded')}
          </span>
        )}
        {templateUploadedAt ? (
          <span className="text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
            {t('companies.dkp.uploadedAt', { date: new Date(templateUploadedAt).toLocaleString(i18n.language === 'en' ? 'en-US' : 'ru-RU') })}
          </span>
        ) : null}
        {editable ? (
          <>
            <label className="cursor-pointer text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-accent-orange)] hover:underline">
              {templatePath ? t('docUploader.replace') : t('docUploader.upload')}
              <input
                type="file"
                accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                disabled={busy}
                onChange={(e) => {
                  void handleFile(e.target.files?.[0] ?? null)
                  e.target.value = ''
                }}
                className="hidden"
              />
            </label>
            {templatePath ? (
              <Button variant="outline" onClick={handleDelete} disabled={busy}>
                {t('companies.dkp.delete')}
              </Button>
            ) : null}
          </>
        ) : null}
      </div>
      {!templatePath ? (
        <span className="text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
          {t('companies.dkp.noTemplateWarning')}
        </span>
      ) : null}
      {busy ? (
        <span className="text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
          {t('common.loading')}
        </span>
      ) : null}
      {error ? (
        <span className="text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-accent-red)]">
          {error}
        </span>
      ) : null}
    </div>
  )
}
